import { useEffect, useState } from 'react';
import _ from 'lodash';

export enum ValidatorType {
  REQUIRED,
  MAX_LENGTH,
  MIN,
  MAX,
  INTEGER,
  INVALID_LEADING_SPACES,
  INVALID_CHARACTERS,
  INVALID_CHARACTERS_SHORT,
  INVALID_CHARACTERS_MEDIUM,
  INVALID_LEADING_CHARACTER,
  REQUIRED_IGNORE_INIT_CARET,
  VALUE_FOUND_ON_ARRAY,
  INVALID_PORT_RANGE,
  INVALID_INIT_CHARACTER,
  REQUIRED_CONDITIONAL,
  // IMPLEMENT MORE AS NEEDED
}

export interface Validator {
  type: ValidatorType;
  value?: any;
  invalidMessage: string;
  range?: string;
}

export interface CallbackFn {
  value: string | number;
  onPaste: boolean;
  isValid: boolean;
}

export interface Validation {
  isValid: boolean;
  invalidMessage: string;
}

export interface UseFormInput {
  value: string | number;
  onChange: (event: any) => void;
  valid: boolean;
  invalidMessage: string;
  /** Flags if this field was modified by the user */
  dirty: boolean;
  /** Callback function when the value is changed */
  cb: (fn: CallbackFn) => void;
  /** Allows for explicit overriding of the current value */
  overrideValue: (value: string | number) => void;
  resetValue: (value: string | number) => void;
  /** Allows explicit overriding of validation */
  overrideValidation: (validation: Validation) => void;
  validate: (val?: string) => boolean;
  validating: boolean;
}

export const defaultUseFormInput: UseFormInput = {
  value: '',
  onChange: () => _.noop(),
  valid: true,
  invalidMessage: '',
  dirty: false,
  cb: () => _.noop(),
  overrideValue: () => _.noop(),
  resetValue: () => _.noop(),
  overrideValidation: () => _.noop(),
  validate: () => true,
  validating: false,
};

/**
 * Provides custom hook to do form field validations
 */
export const useFormInput = (
  initialValue: string | number,
  cb: (fn: CallbackFn) => void,
  ...validators: Validator[]
): UseFormInput => {
  const isNumber = (val: any): boolean => {
    return val != null && val - parseFloat(val) + 1 >= 0;
  };

  const convertToStringAndTrim = (val: string | number) => {
    return (val + '').trim();
  };

  const isMax = (userValue: string | number, validatorValue: number) => {
    return userValue <= validatorValue;
  };

  const isMin = (userValue: string | number, validatorValue: number) => {
    return userValue >= validatorValue;
  };

  const allNumeric = (userValue: string | number) => {
    const re = /^[0-9\b]+$/;
    return re.test(convertToStringAndTrim(userValue));
  };

  const splitAndValidateRange = (userValue: string | number, range: string) => {
    if (!_.isEmpty(range)) {
      const portArray = range.split('-');
      return !isMin(userValue, parseInt(portArray[0])) || !isMax(userValue, parseInt(portArray[1]));
    }
  };

  const [value, setValue] = useState(initialValue);
  const [invalidMessage, setInvalidMessage] = useState('');

  // We need to check whether the message is different before changing the state and causing re-render
  const doSetInvalidMessage = (message: string) => {
    if (invalidMessage !== message) {
      setInvalidMessage(message);
    }
  };

  const doValidate = (val: string | number) => {
    if (validators.length === 0) {
      return true;
    }
    return !validators.some(v => {
      switch (v.type) {
        case ValidatorType.REQUIRED:
          if (val === null || (convertToStringAndTrim(val) === '' && !isNumber(val))) {
            doSetInvalidMessage(v.invalidMessage);
            return true;
          }
          break;
        case ValidatorType.REQUIRED_CONDITIONAL:
          if ((val === null || (convertToStringAndTrim(val) === '' && !isNumber(val))) && v.value === true) {
            doSetInvalidMessage(v.invalidMessage);
            return true;
          }
          break;
        case ValidatorType.MAX_LENGTH:
          if (!_.isEmpty(val) && convertToStringAndTrim(val).length > v.value) {
            doSetInvalidMessage(v.invalidMessage);
            return true;
          }
          break;
        case ValidatorType.MAX:
          if (!_.isEmpty(val) && !isMax(val, v.value)) {
            doSetInvalidMessage(v.invalidMessage);
            return true;
          }
          break;
        case ValidatorType.MIN:
          if (!_.isEmpty(val) && !isMin(val, v.value)) {
            doSetInvalidMessage(v.invalidMessage);
            return true;
          }
          break;
        case ValidatorType.INTEGER:
          if (!_.isEmpty(val) && !allNumeric(val)) {
            doSetInvalidMessage(v.invalidMessage);
            return true;
          }
          break;

        case ValidatorType.INVALID_PORT_RANGE:
          if (!_.isEmpty(val) && splitAndValidateRange(val, v.range ? v.range : '')) {
            doSetInvalidMessage(v.invalidMessage);
            return true;
          }
          break;

        case ValidatorType.INVALID_LEADING_SPACES:
          if (!_.isEmpty(val) && ('' + val).startsWith(' ')) {
            doSetInvalidMessage(v.invalidMessage);
            return true;
          }
          break;
        case ValidatorType.INVALID_CHARACTERS:
          if (!_.isEmpty(val) && !('' + val).match(v.value ? v.value : '^[a-zA-Z0-9-\\/_&()?!:@#\\s]*$')) {
            doSetInvalidMessage(v.invalidMessage);
            return true;
          }
          break;
        case ValidatorType.INVALID_CHARACTERS_SHORT:
          if (!_.isEmpty(val) && !('' + val).match(v.value ? v.value : '^[a-zA-Z0-9._-]+$')) {
            doSetInvalidMessage(v.invalidMessage);
            return true;
          }
          break;
        case ValidatorType.INVALID_CHARACTERS_MEDIUM:
          if (!_.isEmpty(val) && !('' + val).match(v.value ? v.value : '^[a-zA-Z_][a-zA-Z0-9_]*$')) {
            doSetInvalidMessage(v.invalidMessage);
            return true;
          }
          break;
        case ValidatorType.INVALID_LEADING_CHARACTER:
          if (!_.isEmpty(val) && !('' + val).substr(0, 1).match(v.value)) {
            doSetInvalidMessage(v.invalidMessage);
            return true;
          }
          break;
        case ValidatorType.REQUIRED_IGNORE_INIT_CARET:
          if (
            val !== null &&
            val !== undefined &&
            val.toString().length !== 0 &&
            convertToStringAndTrim(val) === '' &&
            !isNumber(val)
          ) {
            doSetInvalidMessage(v.invalidMessage);
            return true;
          }
          break;
        case ValidatorType.VALUE_FOUND_ON_ARRAY:
          let invalid = false;
          if (Array.isArray(v.value) && v.value.length > 0) {
            for (const string of v.value) {
              if (string.toLocaleLowerCase() === convertToStringAndTrim(val).toLowerCase()) {
                invalid = true;
                doSetInvalidMessage(v.invalidMessage);
                break;
              }
            }
          }
          return invalid;
        case ValidatorType.INVALID_INIT_CHARACTER:
          if (
            val !== null &&
            val !== undefined &&
            val.toString().length !== 0 &&
            convertToStringAndTrim(val).charAt(0) !== v.value
          ) {
            doSetInvalidMessage(v.invalidMessage);
            return true;
          }
          return false;
      }
      return false;
    });
  };

  const [valid, setValid] = useState(doValidate(initialValue));
  const [dirty, setDirty] = useState(false);
  // indicates when the field is manually validated, i.e. not while changing the value to show the error immediately
  const [validating, setValidating] = useState(false);
  const [isOnPaste, setIsOnPaste] = useState(false);

  // Triggers manual validation
  // val can be passed in case we hold function reference and the value still holds the initialValue
  const validate = (val?: string): boolean => {
    setValid(doValidate(!_.isNil(val) ? val : value));
    setValidating(true);
    setDirty(true);
    return valid;
  };

  useEffect(() => {
    if (valid) {
      setInvalidMessage('');
    }
    if (dirty) {
      cb({
        value: value,
        onPaste: isOnPaste,
        isValid: valid,
      });
    }
  }, [value, dirty, isOnPaste, valid]);

  const overrideValue = (val: string | number) => {
    if (val !== value) {
      setValue(val);
    }
  };

  /**
   * Override of the validation. Useful when backend validation is in place.
   * @param validity
   */
  const overrideValidation = (validity: Validation) => {
    setInvalidMessage(validity.invalidMessage);
    setValid(validity.isValid);
  };

  const resetValue = (val: string | number) => {
    setValue(val);
    setValid(doValidate(val ? val : value));
    setValidating(true);
    setDirty(true);
  };

  const inputChangeHandler = (event: any) => {
    const target = event.target as HTMLInputElement;
    if (event.target.validity.valid) {
      setValue(target.value);
      setValid(doValidate(target.value));
      setDirty(true);
      setIsOnPaste(event.nativeEvent.inputType === 'insertFromPaste');
    }
  };

  return {
    value: value,
    onChange: inputChangeHandler,
    cb: cb,
    valid: valid,
    invalidMessage: invalidMessage,
    dirty: dirty,
    validate: validate,
    resetValue: resetValue,
    validating: validating,
    overrideValue: overrideValue,
    overrideValidation: overrideValidation,
  };
};
